import React, { Dispatch, SetStateAction, useEffect, useState } from "react";

import { Modal } from "./Modal";

import { HeaderModal } from "./Header";
import { FooterModal } from "./Footer";
import { Body } from "./Body";

import allImgPaths from "assets/images";
import { InputTextField } from "components/InputField";
import Textarea from "../Textarea";
import ClaimTreeSelector from "../../ClaimTreeSelector";
import { startVijilize, estimateVijilization, getPatentDetails } from "services/project";
import { getInfraCost } from "services/configuration";
import Toast from "../Toast";
import { VIJILIZE_CONSTANTS } from "utils/constant";
interface Iprops {
  isShow: boolean;
  setIsShow: Dispatch<SetStateAction<boolean>> | ((val: boolean) => void);
  setRefreshData: Dispatch<SetStateAction<boolean>>;
  selectedSource: any[];
  selectedComparePatents: any[];
  selectedCompareFiles: any[];
  selectedCompareUrls: any[];
  projectId: any;
  sourcePatents: any[];
  comparePatents: any[];
  compareFiles: any[];
  compareUrls: any[];
  defaultName?: string;
  defaultDescription?: string;
}

function VijilizeModal({
  isShow,
  setIsShow,
  setRefreshData,
  selectedSource,
  selectedComparePatents,
  selectedCompareFiles,
  selectedCompareUrls,
  projectId,
  sourcePatents,
  comparePatents,
  compareFiles,
  compareUrls,
  defaultName = "",
  defaultDescription = "",
}: Iprops) {
  const [vijilizeDescription, setVijilizeDescription] = useState<string>("");
  const [vijilizeName, setVijilizeName] = useState<string>("");
  const [useOnlyIndependentClaims, setUseOnlyIndependentClaims] = useState<boolean>(false);
  const [selectSpecificClaims, setSelectSpecificClaims] = useState<boolean>(false);
  const [combineMainUrl, setCombineMainUrl] = useState<boolean>(true);
  const [estimate, setEstimate] = useState<any>(null);
  const [estimateLoading, setEstimateLoading] = useState<boolean>(false);
  const [infraCostMultiplier, setInfraCostMultiplier] = useState<number>(50);
  // patent_id -> { claimKeys: string[], loading: boolean, structure?: string }
  const [patentClaims, setPatentClaims] = useState<Record<string, { claimKeys: string[]; loading: boolean; structure?: string }>>({});
  // patent_id -> selected claim keys
  const [selectedClaims, setSelectedClaims] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (isShow) {
      // Pre-fill name/description from defaults when modal opens
      if (defaultName) setVijilizeName(defaultName);
      if (defaultDescription) setVijilizeDescription(defaultDescription);
    } else {
      // Reset state when modal is closed
      setVijilizeDescription("");
      setVijilizeName("");
      setUseOnlyIndependentClaims(false);
      setSelectSpecificClaims(false);
      setCombineMainUrl(true);
      setEstimate(null);
      setEstimateLoading(false);
      setPatentClaims({});
      setSelectedClaims({});
    }
  }, [isShow]);

  // Fetch claims for all source patents when "Select specific claims" is enabled
  useEffect(() => {
    if (!selectSpecificClaims || !isShow) return;

    filteredSourcePatents.forEach((patent: any) => {
      const pid = patent.patent_id;
      if (patentClaims[pid]) return; // already fetched

      setPatentClaims((prev) => ({ ...prev, [pid]: { claimKeys: [], loading: true } }));

      getPatentDetails(pid)
        .then((res: any) => {
          const data = res?.data || res || {};
          const claims = data.claims || {};
          const structure = data.structure || "";
          const keys = Object.keys(claims).sort((a, b) => {
            const numA = parseInt(a.replace(/\D/g, "")) || 0;
            const numB = parseInt(b.replace(/\D/g, "")) || 0;
            return numA - numB;
          });
          setPatentClaims((prev) => ({ ...prev, [pid]: { claimKeys: keys, loading: false, structure } }));
          // Start with nothing selected
          setSelectedClaims((prev) => ({ ...prev, [pid]: [] }));
        })
        .catch(() => {
          setPatentClaims((prev) => ({ ...prev, [pid]: { claimKeys: [], loading: false } }));
        });
    });
  }, [selectSpecificClaims, isShow]);

  useEffect(() => {
    if (isShow && selectedSource.length > 0) {
      const targetDocCount = selectedComparePatents.length + selectedCompareFiles.length;
      const targetUrlCount = selectedCompareUrls.length;

      if (targetDocCount + targetUrlCount > 0) {
        setEstimateLoading(true);

        // Fetch both estimate and infra cost multiplier in parallel
        Promise.all([
          estimateVijilization({
            source_patent_ids: selectedSource,
            target_document_count: targetDocCount,
            target_url_count: targetUrlCount,
          }),
          getInfraCost(),
        ])
          .then(([estimateRes, infraRes]: any[]) => {
            setEstimate(estimateRes);
            const multiplier = infraRes?.data?.ipvijil_infra_cost ?? 50;
            setInfraCostMultiplier(multiplier);
          })
          .catch((err: any) => {
            console.error("Estimate API failed:", err);
            setEstimate(null);
          })
          .finally(() => {
            setEstimateLoading(false);
          });
      }
    }
  }, [isShow, selectedSource, selectedComparePatents, selectedCompareFiles, selectedCompareUrls]);

  let filteredSourcePatents = sourcePatents.filter((item: any) =>
    selectedSource.includes(item.patent_id)
  );
  let filteredComparePatents = comparePatents.filter((item: any) =>
    selectedComparePatents.includes(item.patent_id)
  );
  let filteredCompareFiles = compareFiles.filter((item: any) =>
    selectedCompareFiles.includes(item.patent_id)
  );
  let filteredCompareUrls = compareUrls.filter((item: any) =>
    selectedCompareUrls.includes(item._id)
  );
  let dataUsed = [
    { type: "source", data: [...filteredSourcePatents] },
    {
      type: "compare",
      data: [
        ...filteredComparePatents,
        ...filteredCompareFiles,
        ...filteredCompareUrls,
      ],
    },
  ];

  // Parse Mermaid structure to get child -> parent mapping
  const parseMermaidParentMap = (structure: string): Record<string, string> => {
    const parentMap: Record<string, string> = {};
    // Map nodeId (e.g. A1, B) -> claimKey (e.g. claim1, claim9)
    const nodeMap: Record<string, string> = {};
    const nodeRegex = /([A-Za-z][A-Za-z0-9]*)\s*\[\s*"?\s*Claim\s+(\d+)/gi;
    let match;
    while ((match = nodeRegex.exec(structure)) !== null) {
      nodeMap[match[1]] = `claim${match[2]}`;
    }
    // Parse edges: parentId --> childId
    const edgeRegex = /([A-Za-z][A-Za-z0-9]*)\s*-->\s*([A-Za-z][A-Za-z0-9]*)/g;
    while ((match = edgeRegex.exec(structure)) !== null) {
      const parentClaim = nodeMap[match[1]];
      const childClaim = nodeMap[match[2]];
      if (parentClaim && childClaim) {
        parentMap[childClaim] = parentClaim;
      }
    }
    return parentMap;
  };

  // Get all ancestors of a claim (walk up the tree)
  const getAncestors = (claimKey: string, parentMap: Record<string, string>): string[] => {
    const ancestors: string[] = [];
    let current = claimKey;
    while (parentMap[current]) {
      ancestors.push(parentMap[current]);
      current = parentMap[current];
    }
    return ancestors;
  };

  const toggleClaimSelection = (patentId: string, claimKey: string) => {
    setSelectedClaims((prev) => {
      const current = prev[patentId] || [];
      if (current.includes(claimKey)) {
        return { ...prev, [patentId]: current.filter((k) => k !== claimKey) };
      } else {
        // Auto-select all ancestors when selecting a dependent claim
        const structure = patentClaims[patentId]?.structure;
        let toSelect = [claimKey];
        if (structure) {
          const parentMap = parseMermaidParentMap(structure);
          toSelect = [...toSelect, ...getAncestors(claimKey, parentMap)];
        }
        const newSelection = [...new Set([...current, ...toSelect])];
        return { ...prev, [patentId]: newSelection };
      }
    });
  };

  const toggleAllClaims = (patentId: string) => {
    const allKeys = patentClaims[patentId]?.claimKeys || [];
    const current = selectedClaims[patentId] || [];
    if (current.length === allKeys.length) {
      setSelectedClaims((prev) => ({ ...prev, [patentId]: [] }));
    } else {
      setSelectedClaims((prev) => ({ ...prev, [patentId]: [...allKeys] }));
    }
  };

  const [activeIndex, setActiveIndex] = useState<number>(0);

  const toggleAccordion = (index: number) => {
    setActiveIndex(activeIndex === index ? 0 : index);
  };

  const handleProjectDescriptionChange = (e: any) => {
    setVijilizeDescription(e.target.value);

    let getTextArea = document.getElementById("project-desc");
    if (getTextArea) {
      if (e.target.value.length === 0) {
        e.target.style.height = "24px";
      } else {
        e.target.style.height = "auto";
        e.target.style.height = e.target.scrollHeight + "px";
      }
    }
  };
  const handleNameChange = (e: any) => {
    setVijilizeName(e.target.value);
  };
  const transformPatentDataArray = (
    inputDataArray: any[],
    additionalData: any
  ): any[] => {
    try {
      const transformedArray = inputDataArray.map((inputData) => {
        const { patent_id, file_id, s3_file_path, key } = inputData;
        const { team_id, project_id } = additionalData;

        return {
          team_id,
          project_id,
          file_id,
          patent_id,
          key,
          s3_file_path,
        };
      });

      return transformedArray;
    } catch (error) {
      console.error("Error transforming patent data array:", error);
      throw new Error("Failed to transform patent data array");
    }
  };
  const transformPatentDataArrayWithoutAdditional = (
    inputDataArray: any[]
  ): any[] => {
    try {
      const transformedArray = inputDataArray.map((inputData) => {
        if (inputData?.url && inputData?.last_crawl_status) {
          return inputData;
        }
        const { patent_id, file_id, s3_file_path, key } = inputData;

        return {
          file_id,
          patent_id,
          key,
          s3_file_path,
        };
      });

      return transformedArray;
    } catch (error) {
      console.error("Error transforming patent data array:", error);
      throw new Error("Failed to transform patent data array");
    }
  };

  const startVijilizeProcess = async () => {
    let selectedTeam: any = JSON.parse(
      localStorage.getItem("selectedTeam") as string
    );

    let sourcePayload = [...filteredSourcePatents];
    let comparePayload = [
      ...filteredComparePatents,
      ...filteredCompareFiles,
      ...filteredCompareUrls,
    ];

    let sourceVijilizeArr: any[];
    let compareVijilizeObj: any;
    let payload: any;

    let team_id: string = selectedTeam.currentTeamId;
    const project_id = projectId;

    if (comparePayload?.length) {
      compareVijilizeObj = {
        name: vijilizeName,
        description: vijilizeDescription,
        source: transformPatentDataArrayWithoutAdditional([...sourcePayload]),
        compare: transformPatentDataArrayWithoutAdditional([...comparePayload]),
        team_id: team_id,
        project_id: project_id,
        useOnlyIndependentClaims: useOnlyIndependentClaims,
        combineMainUrl: combineMainUrl,
        ...(selectSpecificClaims && { selectedClaims }),
      };
      payload = {
        type: VIJILIZE_CONSTANTS.COMPARER,
        data: compareVijilizeObj,
      };
    } else {
      sourceVijilizeArr = transformPatentDataArray([...sourcePayload], {
        team_id,
        project_id,
      });
      payload = {
        type: VIJILIZE_CONSTANTS.ANALYZER,
        data: sourceVijilizeArr,
      };
    }
    try {
      await startVijilize(payload);
      Toast.success("Success", "Vijilization started");
      setRefreshData(true);
      setIsShow(false);
    } catch (error: any) {
      Toast.error("Error", error?.message || "Failed to start vijilization");
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    startVijilizeProcess();
  };
  const onCloseModal = () => {
    setIsShow(false);
  };
  const isSubmitDisabled = () => {
    if (vijilizeName.trim() === "") return true;
    if (selectSpecificClaims) {
      const totalSelected = Object.values(selectedClaims).reduce((sum, keys) => sum + keys.length, 0);
      if (totalSelected === 0) return true;
    }
    return false;
  };

  const renderAccordionItem = (item: any, index: number, type: string) => {
    const { itemType, data } = item;

    return (
      <div
        key={index}
        onClick={() => toggleAccordion(index)}
        className={`mb-3 last:mb-0 ${
          activeIndex === index ? "" : "accordion-hide"
        }`}
      >
        {/* Header Section */}
        <div
          className={`border border-[#F0F0F0] px-2 py-2 bg-[#FDFDFD] cursor-pointer accordion-heading flex items-center justify-between`}
        >
          <div className="flex items-center gap-4 w-full justify-between">
            <p className="text-[#555F6D] text-sm font-interMedium">
              {type.charAt(0).toUpperCase() + type.slice(1)}{" "}
              <span className="text-[#555F6D] text-sm font-interMedium">
                ({data.length})
              </span>
            </p>
            <img
              onClick={(e) => {
                e.stopPropagation();
                toggleAccordion(index);
              }}
              className="accordion-arrow cursor-pointer hover:bg-[#f2f4f7] rounded w-5"
              src={allImgPaths.dropdownArrow}
              alt="Toggle Dropdown"
            />
          </div>
        </div>

        {/* Content Section */}
        <div
          className={`accordion-content border border-[#F0F0F0] border-t-0 ${
            activeIndex === index ? "block" : "hidden"
          }`}
        >
          {type === "source" &&
            data.map((item: any, itemIndex: any) => (
              <div key={itemIndex} className="py-3 ml-3">
                <p
                  title={item.file_name}
                  className="text-sm font-interSemiBold text-[#1751D0] truncate max-w-[calc(100%_-_15px)] w-fit cursor-pointer"
                >
                  {item.file_name}
                </p>

                {/* Claim tree/chips when selectSpecificClaims is enabled */}
                {selectSpecificClaims && (
                  <div className="mt-2">
                    {patentClaims[item.patent_id]?.loading ? (
                      <span className="text-xs text-[#7F7D83]">Loading claims...</span>
                    ) : patentClaims[item.patent_id]?.claimKeys?.length > 0 ? (
                      <>
                        {/* All / Deselect All toggle */}
                        <div className="mb-2">
                          <button
                            type="button"
                            onClick={() => toggleAllClaims(item.patent_id)}
                            className={`px-2 py-0.5 rounded-full text-xs font-interMedium border transition-colors ${
                              (selectedClaims[item.patent_id]?.length || 0) === patentClaims[item.patent_id]?.claimKeys.length
                                ? "bg-[#1751D0] text-white border-[#1751D0]"
                                : "bg-white text-[#4F4D55] border-[#D0D5DD] hover:border-[#1751D0]"
                            }`}
                          >
                            All
                          </button>
                        </div>

                        {/* Render tree if structure is available, otherwise fall back to chips */}
                        {patentClaims[item.patent_id]?.structure ? (
                          <ClaimTreeSelector
                            structure={patentClaims[item.patent_id].structure!}
                            claimKeys={patentClaims[item.patent_id].claimKeys}
                            selectedClaims={selectedClaims[item.patent_id] || []}
                            onToggleClaim={(claimKey) => toggleClaimSelection(item.patent_id, claimKey)}
                          />
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {patentClaims[item.patent_id].claimKeys.map((claimKey: string) => {
                              const isSelected = selectedClaims[item.patent_id]?.includes(claimKey);
                              const claimNum = claimKey.replace(/\D/g, "");
                              return (
                                <button
                                  key={claimKey}
                                  type="button"
                                  onClick={() => toggleClaimSelection(item.patent_id, claimKey)}
                                  className={`px-2 py-0.5 rounded-full text-xs font-interMedium border transition-colors ${
                                    isSelected
                                      ? "bg-[#1751D0] text-white border-[#1751D0]"
                                      : "bg-white text-[#4F4D55] border-[#D0D5DD] hover:border-[#1751D0]"
                                  }`}
                                >
                                  Claim {claimNum}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="text-xs text-[#7F7D83]">No claims found</span>
                    )}
                  </div>
                )}
              </div>
            ))}

          {type === "compare" &&
            data.map((item: any, itemIndex: any) => (
              <div className="py-3 ml-3">
                <p
                  title={item?.file_name || item?.url}
                  className="text-sm font-interSemiBold text-[#1751D0] break-words max-w-[calc(100%_-_15px)] cursor-pointer"
                >
                  {item?.file_name ||
                    (item?.url_label ? item?.url_label : item?.url)}
                </p>
              </div>
            ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal className={`modal-wrapper transition-all duration-300 max-h-[90vh] overflow-y-auto ${selectSpecificClaims ? "max-w-[1200px]" : "max-w-[980px]"}`} isShow={isShow}>
        <form onSubmit={handleSubmit}>
          <HeaderModal
            title={"test"}
            isShowHeader={true}
            className="modal-header pt-6 px-6 sticky top-0 bg-white z-10"
          >
            <div className="flex items-center justify-between">
              <img
                className="w-10 h-10"
                src={allImgPaths.serachIcon3}
                alt="remove"
              />
              {/* Close Icon */}
              <img
                className="modal-close modal-toggle cursor-pointer hover:bg-[#f2f4f7] rounded w-5 p-1 mb-4"
                id="modal-toggle"
                src={allImgPaths.closeIcon}
                onClick={onCloseModal}
                alt="close icon"
              />
            </div>
          </HeaderModal>

          <Body title="" isShowBody={true} className="modal-body px-6 pt-4">
            <div className="">
              {/* Heading */}
              <h2 className="text-base font-interSemiBold text-[#0A090B] pb-0.5">
                Vijilize
              </h2>
              <p className="text-sm text-[#4F4D55] mb-4">
                Enter details for this vijilization
              </p>

              {/* Notification */}
              <div className="border border-[#ECECED] rounded-lg px-3 py-3 flex items-start gap-x-2 mb-3">
                <img src={allImgPaths.InfoIconBlue} alt="" />
                <p className="text-sm font-interMedium text-[#0A090B] leading-[1.6]">
                  Each vijilization takes some time to complete. You can find
                  your past and active ones in the Vijilizations tab.
                </p>
              </div>

              {/* Name & Description - side by side when claims tree is shown */}
              {selectSpecificClaims && (
                <div className="flex gap-x-4 mb-4">
                  <div className="flex-1">
                    <span className="text-[#2D2B32] font-interMedium text-sm inline-block pb-2">
                      Name
                    </span>
                    <InputTextField
                      label={""}
                      name={"vijilizeName"}
                      placeholder={"Enter name"}
                      onChange={handleNameChange}
                      value={vijilizeName}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="pb-2 flex items-center justify-between">
                      <span className="text-[#2D2B32] font-interMedium text-sm inline-block">
                        Description
                      </span>
                      <span className="text-xs text-[#7F7D83]">Optional</span>
                    </div>
                    <Textarea
                      name="project-desc"
                      placeholder="Enter description"
                      value={vijilizeDescription}
                      onChange={handleProjectDescriptionChange}
                      id="project-desc"
                    />
                  </div>
                </div>
              )}

              {/* Content */}
              <div className={selectSpecificClaims ? "" : "flex gap-x-8"}>
                <div className={`px-3 py-3 rounded-lg border border-[#E6E6E6] ${selectSpecificClaims ? "w-full" : "w-[434px]"}`}>
                  <h4 className="text-sm font-interMedium text-[#0A090B] pb-3">
                    Data Used
                  </h4>

                  {/* Use only Independent Claims checkbox */}
                  <div className="mb-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={useOnlyIndependentClaims}
                        onChange={(e) => {
                          setUseOnlyIndependentClaims(e.target.checked);
                          if (e.target.checked) {
                            setSelectSpecificClaims(false);
                            setSelectedClaims({});
                          }
                        }}
                        className="w-4 h-4 text-[#1751D0] border border-gray-300 rounded focus:ring-[#1751D0] focus:ring-2"
                        disabled={selectSpecificClaims}
                      />
                      <span className={`text-sm font-interMedium ${selectSpecificClaims ? "text-[#9E9DA2]" : "text-[#0A090B]"}`}>
                        Use only Independent Claims
                      </span>
                    </label>
                  </div>

                  {/* Select specific claims checkbox */}
                  <div className="mb-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectSpecificClaims}
                        onChange={(e) => {
                          setSelectSpecificClaims(e.target.checked);
                          if (e.target.checked) {
                            setUseOnlyIndependentClaims(false);
                            setActiveIndex(0); // auto-expand source accordion
                          } else {
                            setSelectedClaims({});
                            setPatentClaims({});
                          }
                        }}
                        className="w-4 h-4 text-[#1751D0] border border-gray-300 rounded focus:ring-[#1751D0] focus:ring-2"
                        disabled={useOnlyIndependentClaims}
                      />
                      <span className={`text-sm font-interMedium ${useOnlyIndependentClaims ? "text-[#9E9DA2]" : "text-[#0A090B]"}`}>
                        Select claims
                      </span>
                    </label>
                  </div>

                  {/* Accordion */}
                  <div className={`overflow-y-auto pr-2 ${selectSpecificClaims ? "max-h-[400px]" : "max-h-80"}`}>
                    {dataUsed.map((item, index) =>
                      renderAccordionItem(item, index, item.type)
                    )}
                  </div>

                  {/* Combine main url checkbox - show only if there's compare data */}
                  {(filteredComparePatents.length > 0 || filteredCompareFiles.length > 0 || filteredCompareUrls.length > 0) && (
                    <div className="mt-3 pt-3 border-t border-[#E6E6E6]">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={combineMainUrl}
                          onChange={(e) => setCombineMainUrl(e.target.checked)}
                          className="w-4 h-4 text-[#1751D0] border border-gray-300 rounded focus:ring-[#1751D0] focus:ring-2"
                        />
                        <span className="text-sm font-interMedium text-[#0A090B]">
                          Combine main url
                        </span>
                      </label>
                    </div>
                  )}
                </div>

                {/* Name & Description - right column when claims tree is NOT shown */}
                {!selectSpecificClaims && (
                  <div className="w-1/2 flex-shrink-0">
                    <div className="mb-6">
                      <span className="text-[#2D2B32] font-interMedium text-sm inline-block pb-2">
                        Name
                      </span>
                      <InputTextField
                        label={""}
                        name={"vijilizeName"}
                        placeholder={"Enter name"}
                        onChange={handleNameChange}
                        value={vijilizeName}
                      />
                    </div>

                    <div className="">
                      <div className="pb-2 flex items-center justify-between">
                        <span className="text-[#2D2B32] font-interMedium text-sm inline-block ">
                          Description
                        </span>
                        <span className="text-xs text-[#7F7D83]">Optional</span>
                      </div>
                      <Textarea
                        name="project-desc"
                        placeholder="Enter description"
                        value={vijilizeDescription}
                        onChange={handleProjectDescriptionChange}
                        id="project-desc"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Estimation */}
              <div className="mt-4 flex items-center justify-end gap-x-4">
                {estimateLoading ? (
                  <span className="text-xs text-[#7F7D83]">
                    Calculating estimate...
                  </span>
                ) : estimate ? (
                  estimate.total_patents > estimate.analyzed_patents ? (
                    <>
                      <div className="flex items-center gap-x-1.5">
                        <span className="text-xs text-[#7F7D83]">Estimated credits:</span>
                        <span className="text-xs font-interMedium text-[#4F4D55]">N/A</span>
                      </div>
                      <div className="flex items-center gap-x-1.5">
                        <span className="text-xs text-[#7F7D83]">Estimated time:</span>
                        <span className="text-xs font-interMedium text-[#4F4D55]">N/A</span>
                      </div>
                      <div className="relative group">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="#7F7D83" className="w-3.5 h-3.5 cursor-help">
                          <path fillRule="evenodd" d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM8 4a.75.75 0 0 1 .75.75v.5a.75.75 0 0 1-1.5 0v-.5A.75.75 0 0 1 8 4Zm0 3a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 8 7Z" clipRule="evenodd" />
                        </svg>
                        <div className="absolute bottom-full right-0 mb-1.5 hidden group-hover:block w-56 bg-[#1E1E1E] text-white text-xs rounded-md px-3 py-2 leading-relaxed z-50 shadow-lg">
                          Estimates are unavailable. Please analyze all source patents to enable cost and time projections.
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-x-1.5">
                        <span className="text-xs text-[#7F7D83]">Estimated credits:</span>
                        <span className="text-xs font-interMedium text-[#4F4D55]">
                          ~{Math.ceil(estimate.estimated_cost_usd * infraCostMultiplier)}
                        </span>
                      </div>
                      <div className="flex items-center gap-x-1.5">
                        <span className="text-xs text-[#7F7D83]">Estimated time:</span>
                        <span className="text-xs font-interMedium text-[#4F4D55]">
                          ~{estimate.estimated_time_display}
                        </span>
                      </div>
                      <div className="relative group">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="#7F7D83" className="w-3.5 h-3.5 cursor-help">
                          <path fillRule="evenodd" d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM8 4a.75.75 0 0 1 .75.75v.5a.75.75 0 0 1-1.5 0v-.5A.75.75 0 0 1 8 4Zm0 3a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 8 7Z" clipRule="evenodd" />
                        </svg>
                        <div className="absolute bottom-full right-0 mb-1.5 hidden group-hover:block w-56 bg-[#1E1E1E] text-white text-xs rounded-md px-3 py-2 leading-relaxed z-50 shadow-lg">
                          These are approximate estimates and may vary depending on patent complexity, number of claim elements, and target content.
                        </div>
                      </div>
                    </>
                  )
                ) : (
                  <span className="text-xs text-[#7F7D83]">
                    Estimated credits: --
                  </span>
                )}
              </div>
            </div>
          </Body>

          <FooterModal
            title=""
            isShowFooter={true}
            className="modal-footer pt-8 px-6 pb-4 sticky bottom-0 bg-white z-10"
          >
            <div className="flex items-center gap-x-3 justify-center">
              <button
                type="button"
                className="text-[#344054] font-interSemiBold border border-[#D0D5DD] rounded-lg tracking-wide py-2.5 px-2.5 !text-base flex-1 hover:bg-[#f2f4f7]"
                onClick={onCloseModal}
              >
                Cancel
              </button>

              <button
                className={`!text-base text-white bg-[#1751D0] hover:bg-[#113B98] font-interSemiBold rounded-lg  px-3.5 py-2.5 flex-1 ${
                  isSubmitDisabled() ? "button-disabled" : ""
                } `}
                disabled={isSubmitDisabled()}
                type="submit"
              >
                Vijilize
              </button>
            </div>
          </FooterModal>
        </form>
      </Modal>
    </>
  );
}
export default VijilizeModal;
